import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { materialApi } from '@/lib/api/services/material.api';
import type { UpsertIngredientConversionInput } from '@/types/material-conversion';

function conversionsKey(materialId: number | undefined) {
  return ['materials', 'detail', materialId, 'conversions'] as const;
}

/** Every supplier-item pack-size breakdown saved for one ingredient. */
export function useMaterialConversions(materialId: number | undefined, enabled = true) {
  return useQuery({
    queryKey: conversionsKey(materialId),
    queryFn: () => materialApi.getConversions(materialId as number),
    enabled: enabled && materialId != null,
  });
}

/**
 * A saved conversion re-prices the ingredient (`effectiveUnitCost`) and can
 * change its density, so every material read refetches, not just this list:
 * `['materials']` covers the detail page and the conversions themselves,
 * `['admin', 'materials']` the materials list.
 */
function useInvalidateConversions() {
  const queryClient = useQueryClient();
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['materials'] }),
      queryClient.invalidateQueries({ queryKey: ['admin', 'materials'] }),
    ]);
}

export function useCreateMaterialConversion(materialId: number) {
  const invalidate = useInvalidateConversions();
  return useMutation({
    mutationFn: (data: UpsertIngredientConversionInput) => materialApi.createConversion(materialId, data),
    onSuccess: () => invalidate(),
  });
}

export function useUpdateMaterialConversion(materialId: number) {
  const invalidate = useInvalidateConversions();
  return useMutation({
    mutationFn: ({ conversionId, data }: { conversionId: number; data: UpsertIngredientConversionInput }) =>
      materialApi.updateConversion(materialId, conversionId, data),
    onSuccess: () => invalidate(),
  });
}

export function useDeleteMaterialConversion(materialId: number) {
  const invalidate = useInvalidateConversions();
  return useMutation({
    mutationFn: (conversionId: number) => materialApi.deleteConversion(materialId, conversionId),
    onSuccess: () => invalidate(),
  });
}
